import { PageHeader, LoadingState, DataTable, FilterBar, EmptyState } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import StudentAvatar from '../components/StudentAvatar';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, Grid, Button, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, IconButton, Card, CardContent, Container, Avatar, Stack, Tooltip, Chip, Alert, useMediaQuery, alpha, useTheme, Dialog, DialogTitle, DialogContent, Paper } from '@mui/material';
import {
  CheckCircle, Cancel, Save, CalendarToday,
  Person, Visibility, Group, Restaurant,
  AttachMoney, School, Class, Close, Print
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import api from '../utils/Api';

const days = [
  { label: 'M', key: 'monday' },
  { label: 'T', key: 'tuesday' },
  { label: 'W', key: 'wednesday' },
  { label: 'TH', key: 'thursday' },
  { label: 'F', key: 'friday' }
];

const registerWeekdayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const dayMap = {
  monday: 'M',
  tuesday: 'T',
  wednesday: 'W',
  thursday: 'TH',
  friday: 'F'
};

const reverseDayMap = Object.entries(dayMap).reduce((acc, [k, v]) => {
  acc[v] = k;
  return acc;
}, {});

const registerPrintWidths = {
  number: '6%',
  admission: '12%',
  name: '28%',
  dob: '14%',
  day: '5.5%',
  total: '8.5%',
};

const formatOrdinalDay = (dateInput) => {
  if (!dateInput) return '';

  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '';

  const dayValue = date.getDate();
  const suffix = (dayValue % 10 === 1 && dayValue !== 11) ? 'st' :
    (dayValue % 10 === 2 && dayValue !== 12) ? 'nd' :
      (dayValue % 10 === 3 && dayValue !== 13) ? 'rd' : 'th';

  return `${dayValue}${suffix}`;
};

export default function StudentAttendancePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [week, setWeek] = useState(''); // CHANGED: Empty initial state
  const [term, setTerm] = useState('');
  const [allTerms, setAllTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const userSelectedAcademicYearRef = useRef(false);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newWeekWarning, setNewWeekWarning] = useState(false);
  const [termData, setTermData] = useState(null);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [initializedWeeks, setInitializedWeeks] = useState(() => {
    const saved = localStorage.getItem('initializedWeeks');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [summary, setSummary] = useState({
    totalStudents: 0,
    presentCount: 0,
    absentCount: 0,
    notMarkedCount: 0
  });

  // 🟢 NEW: Per-student request locking
  const [activeRequests, setActiveRequests] = useState({});

  // Register state
  const [registerOpen, setRegisterOpen] = useState(false);
  const [termAttendance, setTermAttendance] = useState([]);
  const [loadingRegister, setLoadingRegister] = useState(false);
  const [maxWeeks, setMaxWeeks] = useState(0);
  const [registerActiveWeek, setRegisterActiveWeek] = useState(1);
  const registerFetchSeqRef = React.useRef(0);

  // FIXED - handles both string and object formats safely
  const schoolId = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return null;

      const user = JSON.parse(raw);

      // Accept either string or object {_id}
      return user.school?._id || user.school || null;
    } catch (err) {
      console.error("Failed to read user from localStorage:", err);
      return null;
    }
  })();

  // Sync initialized weeks to localStorage
  useEffect(() => {
    localStorage.setItem('initializedWeeks', JSON.stringify(Array.from(initializedWeeks)));
  }, [initializedWeeks]);

  // Calculate summary statistics
  useEffect(() => {
    if (students.length > 0) {
      let presentCount = 0;
      let absentCount = 0;
      let notMarkedCount = 0;

      students.forEach(student => {
        const studentAttendance = attendance[student._id] || {};
        days.forEach(day => {
          const status = studentAttendance[day.key];
          if (status === true) presentCount++;
          else if (status === false) absentCount++;
          else notMarkedCount++;
        });
      });

      setSummary({
        totalStudents: students.length,
        presentCount,
        absentCount,
        notMarkedCount
      });
    }
  }, [students, attendance]);

  // Fetch classes
  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      const dataArray = Array.isArray(res.data.classes) ? res.data.classes : [];
      setClasses(dataArray);
      if (dataArray.length > 0) setSelectedClass(dataArray[0]._id);
    } catch (err) {
      console.error('Error fetching classes:', err);
      toast.error('Failed to load classes');
    }
  };

  const fetchAcademicYears = useCallback(async () => {
    if (!schoolId) return;

    try {
      const res = await api.get('/term/years', {
        params: { school: schoolId }
      });

      const years = res.data?.academicYears || [];
      if (years.length > 0) {
        const sortedYears = years.sort((a, b) => b.localeCompare(a));
        setAvailableAcademicYears(sortedYears);
      }
    } catch (err) {
      console.error('Failed to fetch academic years:', err);
      toast.error('Unable to load academic years');
    }
  }, [schoolId]);

  const fetchTerms = useCallback(async () => {
    if (!schoolId) return;

    try {
      const res = await api.get('/term/all', {
        params: { school: schoolId }
      });

      const termsData = res.data?.allTerms || [];
      setAllTerms(termsData);

      const filteredTerms = academicYear
        ? termsData.filter(t => t.academicYear === academicYear)
        : termsData;
      const uniqueTerms = [...new Set(filteredTerms.map(t => t.term))].sort();
      setAvailableTerms(uniqueTerms);

      if (filteredTerms.length > 0) {
        if (term) {
          const match = filteredTerms.find(t => t.term === term);
          if (match) {
            setSelectedTermId(match._id);
          } else {
            setTerm('');
            setSelectedTermId('');
          }
        } else {
          setSelectedTermId('');
        }
      } else {
        setAvailableTerms([]);
        setTerm('');
        setSelectedTermId('');
      }
    } catch (err) {
      console.error('Failed to fetch terms:', err);
      toast.error('Unable to load terms');
    }
  }, [schoolId, academicYear, term]);

  const fetchTermWeeks = useCallback(async () => {
    if (!term || !academicYear || !selectedTermId) return;

    try {
      const res = await api.get('/term/weeks', {
        params: {
          school: schoolId,
          academicYear,
          term,
          termId: selectedTermId
        }
      });

      const weeks = res.data?.weeks?.map(w => w.label) || [];
      setAvailableWeeks(weeks);
      setTermData(res.data);
      const activeWeekDoc = res.data?.weeks?.find(w => w.isCurrent);
      const activeWeekLabel = activeWeekDoc?.label || (activeWeekDoc ? `Week ${activeWeekDoc.weekNumber}` : '');
      if (weeks.length > 0 && !week && activeWeekDoc) {
        setWeek(activeWeekLabel);
      } else if (weeks.length > 0 && week && !weeks.includes(week)) {
        setWeek(activeWeekLabel);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setAvailableWeeks([]);
        setTermData(null);
        setWeek(''); // Clear week when no weeks available
      } else {
        console.error('Failed to fetch weeks:', err);
        toast.error('Unable to load weeks');
      }
    }
  }, [schoolId, academicYear, term, selectedTermId, week]);

  const fetchStudentsAndAttendance = useCallback(async () => {
    if (!selectedClass || !schoolId || !selectedTermId || !week) {
      return;
    }

    setLoading(true);

    try {
      const res = await api.get('/attendance/by-class', {
        params: {
          classId: selectedClass,
          week,
          term,
          academicYear,
          school: schoolId,
          termId: selectedTermId,
          category: 'feeding',
          _t: new Date().getTime()
        }
      });

      const result = Array.isArray(res.data) ? res.data : [];

      // Map student info
      const studentsData = result.map(r => ({
        _id: r.studentId,
        name: r.name,
        profilePicture: r.profilePicture
      }));

      // Sort alphabetically
      studentsData.sort((a, b) => a.name.localeCompare(b.name));

      // Format attendance state
      const formatted = {};
      result.forEach(r => {
        const fullDays = {};

        if (r.days && typeof r.days === 'object') {
          Object.entries(r.days).forEach(([shortKey, val]) => {
            const fullKey = reverseDayMap[shortKey];
            if (fullKey) {
              fullDays[fullKey] =
                val === 'present'
                  ? true
                  : val === 'absent'
                    ? false
                    : null;
            }
          });
        } else {
          // Set default notmarked for all days
          days.forEach(day => {
            fullDays[day.key] = null;
          });
        }

        formatted[r.studentId] = fullDays;
      });

      const weekKey = `${selectedTermId}-${selectedClass}-${week}`;

      // Check if week has ANY attendance data
      const hasAnyPresentOrAbsent = result.some(record => {
        if (!record.days) return false;
        return Object.values(record.days).some(day =>
          day === 'present' || day === 'absent'
        );
      });

      const savedInitializedWeeks = localStorage.getItem('initializedWeeks');
      const currentInitializedWeeks = savedInitializedWeeks
        ? new Set(JSON.parse(savedInitializedWeeks))
        : new Set();

      const isInitialized = currentInitializedWeeks.has(weekKey) || hasAnyPresentOrAbsent;

      setNewWeekWarning(!isInitialized);
      setStudents(studentsData);
      setAttendance(formatted);

    } catch (err) {
      console.error('❌ Error fetching attendance:', err);

      const weekKey = `${selectedTermId}-${selectedClass}-${week}`;
      const savedInitializedWeeks = localStorage.getItem('initializedWeeks');
      const currentInitializedWeeks = savedInitializedWeeks
        ? new Set(JSON.parse(savedInitializedWeeks))
        : new Set();

      setNewWeekWarning(!currentInitializedWeeks.has(weekKey));

      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [selectedClass, week, term, academicYear, schoolId, selectedTermId]);

  const initializeWeek = async () => {
    setSaving(true);
    try {
      const weekKey = `${selectedTermId}-${selectedClass}-${week}`;

      const response = await api.post('/attendance/initialize-week', {
        classId: selectedClass,
        termId: selectedTermId,
        week: week
      });

      // Update initializedWeeks state and localStorage
      setInitializedWeeks(prev => {
        const newSet = new Set(prev);
        newSet.add(weekKey);
        localStorage.setItem('initializedWeeks', JSON.stringify(Array.from(newSet)));
        return newSet;
      });

      setNewWeekWarning(false);
      fetchStudentsAndAttendance();

      toast.success(response.data?.message || 'Week initialized successfully!');

    } catch (err) {
      console.error('Error initializing week:', err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || 'Failed to initialize week');
    } finally {
      setSaving(false);
    }
  };

  // 🟢 UPDATED: Fast marking with per-student locking and auto-retry
  const handleMark = async (studentId, dayKey, present) => {
    // Prevent concurrent marks for same student
    if (activeRequests[studentId]) return;

    // Set active request for this student
    setActiveRequests(prev => ({ ...prev, [studentId]: true }));

    const weekKey = `${selectedTermId}-${selectedClass}-${week}`;

    // Store previous state for rollback
    const previousAttendance = JSON.parse(JSON.stringify(attendance));

    // 🟢 IMMEDIATE OPTIMISTIC UPDATE
    setAttendance(prev => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        [dayKey]: present
      }
    }));

    // Mark week as initialized
    setInitializedWeeks(prev => {
      const newSet = new Set(prev);
      newSet.add(weekKey);
      localStorage.setItem('initializedWeeks', JSON.stringify(Array.from(newSet)));
      return newSet;
    });

    setNewWeekWarning(false);

    try {
      // Get CURRENT state (which includes our optimistic update)
      const updatedStudentAttendance = {
        ...(attendance[studentId] || {}),
        [dayKey]: present
      };

      // Transform to backend format
      const transformedDays = {};
      days.forEach(day => {
        const value = updatedStudentAttendance[day.key];
        transformedDays[day.label] =
          value === true ? 'present' :
            value === false ? 'absent' : 'notmarked';
      });

      const payload = {
        termId: selectedTermId,
        classId: selectedClass,
        week,
        category: 'feeding',
        attendanceUpdates: [
          {
            studentId: studentId.toString(),
            days: transformedDays
          }
        ]
      };

      let response;
      try {
        response = await api.post('/attendance/mark', payload);
      } catch (err) {
        // 🟢 AUTO-RETRY: If backend says "Request already processing", wait and retry
        if (err.response?.status === 429) {
          console.warn('⚠️ Backend busy, retrying in 600ms...');
          await new Promise(res => setTimeout(res, 600));
          response = await api.post('/attendance/mark', payload);
        } else {
          throw err;
        }
      }

      if (response.data.success) {
        toast.success('Attendance marked successfully!');
      } else {
        throw new Error(response.data.message || 'Unknown backend error');
      }

    } catch (err) {
      console.error('❌ API Error:', err);
      toast.error(err.response?.data?.message || 'Failed to save attendance');

      // 🟢 COMPLETE ROLLBACK on error
      setAttendance(previousAttendance);
    } finally {
      // 🟢 Clear the active request for this student
      setActiveRequests(prev => {
        const copy = { ...prev };
        delete copy[studentId];
        return copy;
      });
    }
  };

  const handleRegisterMark = async (studentId, weekNumber, dayKey, status) => {
    if (activeRequests[studentId]) return;
    setActiveRequests(prev => ({ ...prev, [studentId]: true }));

    const previousTermAttendance = JSON.parse(JSON.stringify(termAttendance));

    // Optimistic Update
    setTermAttendance(prev => {
      return prev.map(student => {
        if (student._id === studentId) {
          const newAttendance = student.attendance ? JSON.parse(JSON.stringify(student.attendance)) : [];
          let weekData = newAttendance.find(a => a.weekNumber === weekNumber);
          if (!weekData) {
            weekData = { weekNumber, days: {}, totalPresent: 0 };
            newAttendance.push(weekData);
          }

          if (status === 'notmarked') {
            weekData.days[dayKey] = 'notmarked';
          } else {
            weekData.days[dayKey] = status ? 'present' : 'absent';
          }

          weekData.totalPresent = Object.values(weekData.days).filter(d => d === 'present').length;
          return { ...student, attendance: newAttendance };
        }
        return student;
      });
    });

    try {
      const student = termAttendance.find(s => s._id === studentId);
      const studentWeekData = student?.attendance?.find(a => a.weekNumber === weekNumber) || { days: {} };

      const transformedDays = {};
      days.forEach(day => {
        const apiDayColumn = reverseDayMap[day.label]; // Should match 'M', 'T', etc.
        let dayStatus = studentWeekData.days[dayMap[apiDayColumn] || apiDayColumn];

        if (apiDayColumn === dayKey || dayMap[apiDayColumn] === dayKey) {
          if (status === 'notmarked') dayStatus = 'notmarked';
          else dayStatus = status ? 'present' : 'absent';
        }

        transformedDays[day.label] = dayStatus || 'notmarked';
      });

      const payload = {
        termId: selectedTermId,
        classId: selectedClass,
        week: `Week ${weekNumber}`,
        weekNumber: weekNumber,
        category: 'feeding',
        attendanceUpdates: [
          {
            studentId,
            days: transformedDays
          }
        ]
      };

      await api.post('/attendance/mark', payload);
      // Optional: sync main attendance state if it's currently showing this week
      if (Number(week.replace('Week ', '')) === weekNumber) {
        fetchStudentsAndAttendance();
      }
    } catch (err) {
      console.error('Register mark error:', err);
      toast.error('Failed to update attendance');
      setTermAttendance(previousTermAttendance);
    } finally {
      setActiveRequests(prev => {
        const copy = { ...prev };
        delete copy[studentId];
        return copy;
      });
    }
  };

  const getTotal = rec => days.reduce((sum, { key }) => sum + (rec[key] === true ? 1 : 0), 0);

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';

  const fetchTermAttendance = useCallback(async () => {
    if (!selectedClass || !academicYear || !term) {
      toast.warning('Select a class, academic year, and term first.');
      return;
    }

    const requestSeq = ++registerFetchSeqRef.current;
    setLoadingRegister(true);
    try {
      const res = await api.get('/attendance/class/term', {
        params: {
          classId: selectedClass,
          academicYear,
          term,
        },
      });

      if (requestSeq !== registerFetchSeqRef.current) return;

      const data = res.data || [];

      data.sort((a, b) => a.name.localeCompare(b.name));

      setTermAttendance(data);

      // Always prefer termData's total weeks if available
      let finalMaxWeeks = termData?.termInfo?.totalWeeks || 0;
      if (finalMaxWeeks === 0) {
        data.forEach(student => {
          student.attendance?.forEach(a => {
            if (a.weekNumber > finalMaxWeeks) finalMaxWeeks = a.weekNumber;
          });
        });
      }
      if (finalMaxWeeks === 0) finalMaxWeeks = 12;

      setMaxWeeks(finalMaxWeeks);

      // Default active week to current week being viewed on page
      if (week) {
        const currentW = parseInt(week.replace('Week ', ''));
        if (!isNaN(currentW)) setRegisterActiveWeek(currentW);
      } else {
        setRegisterActiveWeek(1);
      }
    } catch (err) {
      if (requestSeq !== registerFetchSeqRef.current) return;
      console.error('Failed to fetch term attendance register:', err);
      toast.error('Failed to load register');
    } finally {
      if (requestSeq === registerFetchSeqRef.current) {
        setLoadingRegister(false);
      }
    }
  }, [selectedClass, academicYear, term, termData, week]);

  useEffect(() => {
    if (!registerOpen || !selectedClass || !academicYear || !term) {
      return;
    }

    fetchTermAttendance();
  }, [registerOpen, selectedClass, academicYear, term, fetchTermAttendance]);

  useEffect(() => {
    if (term && allTerms.length > 0) {
      const selectedTerm = allTerms.find(t => t.term === term && t.academicYear === academicYear);
      if (selectedTerm) {
        setSelectedTermId(selectedTerm._id);
      }
    }
  }, [term, academicYear, allTerms]);

  useEffect(() => {
    if (schoolId) {
      // Aggressively pre-fill current term and week using the global timeline endpoint
      api.get('/term/current-week', { params: { school: schoolId } })
        .then(res => {
          const cw = res.data;
          if (userSelectedAcademicYearRef.current) return;
          if (cw?.academicYear && cw?.termName && cw?.termId) {
            setAcademicYear(cw.academicYear);
            setTerm(cw.termName);
            setSelectedTermId(cw.termId);
            setWeek(cw.current > 0 ? `Week ${cw.current}` : '');
          }
        }).catch(err => console.error('Auto-select error:', err));
    }
    fetchAcademicYears();
    fetchClasses();
  }, [schoolId, fetchAcademicYears]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  useEffect(() => {
    fetchTermWeeks();
  }, [fetchTermWeeks]);

  useEffect(() => {
    const handler = debounce(fetchStudentsAndAttendance, 300);
    handler();
    return () => handler.cancel();
  }, [fetchStudentsAndAttendance]);

  // Mobile Student Card Component
  const MobileStudentCard = ({ student }) => {
    const studentAttendance = attendance[student._id] || {};
    const daysPresent = getTotal(studentAttendance);
    const isStudentProcessing = activeRequests[student._id];

    return (
      <Card sx={{ mb: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Student Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" alignItems="center" spacing={1}>
                <StudentAvatar student={student} sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 40,
                  height: 40
                }}>
                  {getInitials(student.name)}
                </StudentAvatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="600">
                    {student.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {daysPresent} days present
                  </Typography>
                </Box>
              </Stack>
              <Chip
                label={daysPresent}
                color={daysPresent > 2 ? "success" : daysPresent > 0 ? "warning" : "error"}
                variant="filled"
                size="small"
                sx={{ fontWeight: 600, minWidth: 40 }}
              />
            </Stack>

            {/* Days Grid - UPDATED GRID v2 */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                ATTENDANCE DAYS
              </Typography>
              <Grid container spacing={1}>
                {days.map(day => (
                  <Grid size={{ xs: 2.4 }} key={day.key}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" fontWeight="600" display="block" gutterBottom>
                        {day.label}
                      </Typography>
                      <Stack direction="row" justifyContent="center" spacing={0.5}>
                        <Tooltip title={isStudentProcessing ? "Saving..." : "Mark Present"}>
                          <span>
                            <IconButton
                              size="small"
                              color={studentAttendance[day.key] === true ? 'success' : 'default'}
                              onClick={() => handleMark(student._id, day.key, true)}
                              disabled={newWeekWarning || isStudentProcessing}
                              sx={{
                                minWidth: 28,
                                minHeight: 28,
                                borderRadius: 1,
                                p: 0.5
                              }}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isStudentProcessing ? "Saving..." : "Mark Absent"}>
                          <span>
                            <IconButton
                              size="small"
                              color={studentAttendance[day.key] === false ? 'error' : 'default'}
                              onClick={() => handleMark(student._id, day.key, false)}
                              disabled={newWeekWarning || isStudentProcessing}
                              sx={{
                                minWidth: 28,
                                minHeight: 28,
                                borderRadius: 1,
                                p: 0.5
                              }}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      <Box
                        sx={{
                          width: '100%',
                          height: 4,
                          bgcolor: studentAttendance[day.key] === true
                            ? theme.palette.success.main
                            : studentAttendance[day.key] === false
                              ? theme.palette.error.main
                              : '#e0e0e0',
                          borderRadius: 2,
                          mt: 0.5
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Status Indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="caption">Present</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Cancel color="error" fontSize="small" />
                <Typography variant="caption">Absent</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: '#e0e0e0',
                    borderRadius: 0.5
                  }}
                />
                <Typography variant="caption">Not Marked</Typography>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: isMobile ? 2 : 4,
        px: isMobile ? 1 : 2
      }}
    >
      {/* Header */}
      <PageHeader title={<>Student Attendance</>} description={<>Track and manage student daily attendance</>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <Group fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Total Students
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                  >
                    {summary.totalStudents}
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                  >
                    Total enrolled
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.success.main,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Present
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    color="success.main"
                    sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                  >
                    {summary.presentCount}
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                  >
                    Present days
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.error.main,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <Cancel fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Absent
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    color="error.main"
                    sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                  >
                    {summary.absentCount}
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                  >
                    Absent days
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.warning.main,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <Visibility fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Not Marked
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    color="warning.main"
                    sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                  >
                    {summary.notMarkedCount}
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                  >
                    Not marked days
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>

      {/* Filter Card */}
      <FilterBar
        sx={{
          mb: 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <CardContent
          sx={{
            p: isMobile ? 2 : isTablet ? 3 : 4
          }}
        >
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 2 : 2}
            sx={{ mb: 3 }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48
              }}
            >
              <CalendarToday fontSize={isMobile ? "small" : "medium"} />
            </Avatar>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="600"
              sx={{ fontSize: isMobile ? '1.125rem' : '1.5rem' }}
            >
              Attendance Setup
            </Typography>
          </Stack>

          {/* Filter Grid - UPDATED GRID v2 */}
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Academic Year */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Year"
                value={academicYear}
                onChange={(e) => {
                  userSelectedAcademicYearRef.current = true;
                  setAcademicYear(e.target.value);
                  setTerm('');
                  setWeek('');
                }}
                disabled={loading || availableAcademicYears.length === 0}
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="" disabled>Select Academic Year</MenuItem>
                {availableAcademicYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Term */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Term"
                value={term}
                onChange={(e) => {
                  setTerm(e.target.value);
                  setWeek('');
                }}
                disabled={loading || availableTerms.length === 0}
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="" disabled>Select Academic Term</MenuItem>
                {availableTerms.map(term => (
                  <MenuItem key={term} value={term}>{term}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Class */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                disabled={loading || classes.length === 0}
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="" disabled>Select Class</MenuItem>
                {classes.map(cls => (
                  <MenuItem key={cls._id} value={cls._id}>
                    {cls.displayName || cls.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Week - FIXED: Check if value is in availableWeeks */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Week"
                value={availableWeeks.includes(week) ? week : ''}
                onChange={(e) => setWeek(e.target.value)}
                disabled={loading || availableWeeks.length === 0}
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
                error={week && !availableWeeks.includes(week)}
                helperText={week && !availableWeeks.includes(week) ? "Selected week is not available" : ""}
              >
                <MenuItem value="" disabled>
                  {availableWeeks.length === 0 ? "No weeks available" : "Select Week"}
                </MenuItem>
                {availableWeeks.map(w => (
                  <MenuItem key={w} value={w}>{w}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </FilterBar>

      {/* UPDATED: Stats Cards with FeedingFeePage Design - UPDATED GRID v2 */}
      

      {/* Term Info */}
      {termData && week && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: isMobile ? 1 : 2 }}>
          <Typography variant="body2">
            {term} {academicYear} • Week {week.replace('Week ', '')} of {termData.termInfo?.totalWeeks}
          </Typography>
        </Alert>
      )}

      {/* Legend */}
      <Card sx={{ mb: 3, boxShadow: isMobile ? 1 : 2 }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 2 : 4}
            alignItems={isMobile ? "flex-start" : "center"}
          >
            <Typography
              variant={isMobile ? "body2" : "subtitle1"}
              fontWeight="600"
              sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
            >
              Legend:
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <CheckCircle color="success" fontSize="small" />
              <Typography variant="body2">Present</Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Cancel color="error" fontSize="small" />
              <Typography variant="body2">Absent</Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: '#e0e0e0',
                  borderRadius: 0.5
                }}
              />
              <Typography variant="body2">Not Marked</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* New Week Warning */}
      {newWeekWarning && week && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: isMobile ? 1 : 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={initializeWeek}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              sx={{ mt: isMobile ? 1 : 0 }}
            >
              {saving ? 'Initializing...' : 'Initialize Week'}
            </Button>
          }
        >
          No attendance records found for this week. Click Initialize Week to start marking attendance.
        </Alert>
      )}

      {/* Register View Trigger */}
      <Card
        sx={{ mb: 3, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05), boxShadow: 3 } }}
        onClick={() => setRegisterOpen(true)}
        elevation={isMobile ? 1 : 2}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems={isMobile ? "flex-start" : "center"} justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                <CalendarToday />
              </Avatar>
              <Box>
                <Typography variant="h6" fontSize={isMobile ? '1rem' : '1.25rem'}>View Full Term Register</Typography>
                <Typography variant="body2" color="text.secondary">Comprehensive term attendance view similar to physical register</Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => { e.stopPropagation(); setRegisterOpen(true); }}
              sx={{ mt: isMobile ? 1 : 0, width: isMobile ? '100%' : 'auto' }}
            >
              Open Register
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Attendance Table */}
      <Card sx={{
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Typography
                variant={isMobile ? "h6" : "h6"}
                fontWeight="600"
                sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
              >
                Student Attendance ({students.length})
              </Typography>
              {students.length > 0 && !isMobile && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    label={`${summary.presentCount} Present`}
                    color="success"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                  <Chip
                    label={`${summary.absentCount} Absent`}
                    color="error"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                  <Chip
                    label={`${summary.notMarkedCount} Not Marked`}
                    color="warning"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          {loading ? (
            <LoadingState />
          ) : students.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <Person
                sx={{
                  fontSize: isMobile ? 48 : 64,
                  color: 'text.secondary',
                  opacity: 0.5,
                  mb: 2
                }}
              />
              <Typography
                variant={isMobile ? "h6" : "h6"}
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
              >
                No students found
              </Typography>
              <Typography
                variant={isMobile ? "body2" : "body2"}
                color="text.secondary"
                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              >
                {selectedClass && term && week
                  ? `No students found in ${classes.find(c => c._id === selectedClass)?.displayName ||
                  classes.find(c => c._id === selectedClass)?.name
                  } for ${week} of ${term} ${academicYear}`
                  : 'Select filters to view students'
                }
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile View - Cards
            <Box sx={{ p: 2 }}>
              <Stack spacing={1}>
                {students.map((student) => (
                  <MobileStudentCard key={student._id} student={student} />
                ))}
              </Stack>
            </Box>
          ) : (
            // Desktop/Tablet View - Table
            <Box sx={{
              width: '100%',
              overflow: 'auto',
              maxHeight: isTablet ? '60vh' : 'none'
            }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STUDENT</TableCell>
                    {days.map(day => (
                      <TableCell key={day.key} align="center" sx={{ fontWeight: 'bold', py: 2 }}>
                        {day.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">DAYS PRESENT</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{students.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {students.map(student => {
                    const studentAttendance = attendance[student._id] || {};
                    const daysPresent = getTotal(studentAttendance);
                    // 🟢 Check if this student has an active request
                    const isStudentProcessing = activeRequests[student._id];

                    return (
                      <TableRow
                        key={student._id}
                        sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <StudentAvatar student={student} sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                              {getInitials(student.name)}
                            </StudentAvatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="500">
                                {student.name}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        {days.map(day => (
                          <TableCell
                            key={day.key}
                            align="center"
                            sx={{
                              py: 1,
                              backgroundColor: studentAttendance[day.key] === undefined || studentAttendance[day.key] === null
                                ? '#f5f5f5'
                                : 'transparent'
                            }}
                          >
                            <Stack direction="row" justifyContent="center" spacing={0.5}>
                              <Tooltip title={isStudentProcessing ? "Saving..." : "Mark Present"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={studentAttendance[day.key] === true ? 'success' : 'default'}
                                    onClick={() => handleMark(student._id, day.key, true)}
                                    disabled={newWeekWarning || isStudentProcessing}
                                    sx={{
                                      minWidth: 32,
                                      minHeight: 32,
                                      borderRadius: 1
                                    }}
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={isStudentProcessing ? "Saving..." : "Mark Absent"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={studentAttendance[day.key] === false ? 'error' : 'default'}
                                    onClick={() => handleMark(student._id, day.key, false)}
                                    disabled={newWeekWarning || isStudentProcessing}
                                    sx={{
                                      minWidth: 32,
                                      minHeight: 32,
                                      borderRadius: 1
                                    }}
                                  >
                                    <Cancel fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        ))}
                        <TableCell sx={{ py: 2 }} align="center">
                          <Chip
                            label={daysPresent}
                            color={daysPresent > 2 ? "success" : daysPresent > 0 ? "warning" : "error"}
                            variant="filled"
                            sx={{ fontWeight: 600, minWidth: 40 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog fullScreen open={registerOpen} onClose={() => setRegisterOpen(false)}>
        {registerOpen && (
          <style>
            {`
              @media print {
                #root, .MuiBackdrop-root { display: none !important; }
                body, html { background-color: white !important; overflow: visible !important; }
                * { scrollbar-width: none !important; }
                ::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
                .MuiDialog-root, .MuiDialog-container, .MuiDialog-paper, .MuiDialogContent-root, .MuiTableContainer-root, .MuiPaper-root { 
                  position: static !important;
                  width: auto !important;
                  max-width: none !important;
                  height: auto !important;
                  max-height: none !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  box-shadow: none !important;
                  border-radius: 0 !important;
                  border: none !important;
                  outline: none !important;
                  background: white !important;
                  transform: none !important;
                  overflow: visible !important;
                  display: block !important;
                }
                .no-print { display: none !important; }
                @page { size: portrait; margin: 6mm; }
                table { page-break-inside: auto; }
                tr { page-break-inside: avoid; page-break-after: auto; }
                thead, .MuiTableHead-root { display: table-row-group !important; }
              }
            `}
          </style>
        )}
        <DialogTitle sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 2 : 0,
          bgcolor: theme.palette.primary.main,
          color: 'white',
          '@media print': {
            display: 'none !important'
          }
        }}>
          <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 2} sx={{ width: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width={isMobile ? '100%' : 'auto'}>
              <Typography variant="h6" fontWeight="bold" fontSize={isMobile ? '1.1rem' : '1.25rem'}>
                {term && academicYear
                  ? `Attendance Register - ${classes.find(c => c._id === selectedClass)?.displayName || classes.find(c => c._id === selectedClass)?.name || ''} - ${term} ${academicYear}`
                  : 'Full Term Attendance Register'}
              </Typography>
              {isMobile && (
                <IconButton onClick={() => setRegisterOpen(false)} sx={{ color: 'white', p: 0 }}><Close /></IconButton>
              )}
            </Box>
            <Chip
              label={`${registerActiveWeek}${['st', 'nd', 'rd'][(registerActiveWeek % 10) - 1] && registerActiveWeek !== 11 && registerActiveWeek !== 12 && registerActiveWeek !== 13 ? ['st', 'nd', 'rd'][(registerActiveWeek % 10) - 1] : 'th'} WEEK`}
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                alignSelf: isMobile ? 'flex-start' : 'auto'
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            className="no-print"
            flexWrap="wrap"
            useFlexGap
            sx={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-start' : 'flex-end', mt: isMobile ? 1 : 0 }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const oldTitle = document.title;
                const classNameStr = classes.find(c => c._id === selectedClass)?.displayName || classes.find(c => c._id === selectedClass)?.name || 'Class';
                const cleanYear = academicYear ? academicYear.replace(/\//g, '-') : '';
                document.title = `Attendance_Register_${classNameStr}_${term}_${cleanYear}`.replace(/[\s\/]+/g, '_');
                window.print();
                document.title = oldTitle;
              }}
              startIcon={<Print />}
              sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: '#f0f0f0' }, flex: isMobile ? '1 1 100%' : 'initial', mb: isMobile ? 1 : 0 }}
            >
              PRINT REGISTER
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setRegisterActiveWeek(prev => Math.max(1, prev - 1))}
              disabled={registerActiveWeek === 1}
              sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: '#f0f0f0' }, flex: isMobile ? 1 : 'initial' }}
            >
              PREV WEEK
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setRegisterActiveWeek(prev => Math.min(maxWeeks, prev + 1))}
              disabled={registerActiveWeek === maxWeeks}
              sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: '#f0f0f0' }, flex: isMobile ? 1 : 'initial' }}
            >
              NEXT WEEK
            </Button>
            {!isMobile && (
              <IconButton onClick={() => setRegisterOpen(false)} sx={{ color: 'white', ml: 1 }}><Close /></IconButton>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'white', display: 'flex', flexDirection: 'column', '@media print': { overflow: 'visible' } }}>
          <Box className="no-print" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Academic Year"
                  value={academicYear}
                  onChange={(e) => {
                    userSelectedAcademicYearRef.current = true;
                    setAcademicYear(e.target.value);
                    setTerm('');
                    setWeek('');
                    setRegisterActiveWeek(1);
                  }}
                  disabled={loading || availableAcademicYears.length === 0}
                  size={isMobile ? 'small' : 'medium'}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="" disabled>Select Academic Year</MenuItem>
                  {availableAcademicYears.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Academic Term"
                  value={term}
                  onChange={(e) => {
                    setTerm(e.target.value);
                    setWeek('');
                    setRegisterActiveWeek(1);
                  }}
                  disabled={loading || availableTerms.length === 0}
                  size={isMobile ? 'small' : 'medium'}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="" disabled>Select Academic Term</MenuItem>
                  {availableTerms.map((termOption) => (
                    <MenuItem key={termOption} value={termOption}>{termOption}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Class"
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  disabled={loading || classes.length === 0}
                  size={isMobile ? 'small' : 'medium'}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="" disabled>Select Class</MenuItem>
                  {classes.map((classItem) => (
                    <MenuItem key={classItem._id} value={classItem._id}>
                      {classItem.displayName || classItem.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {loadingRegister ? (
            <LoadingState />
          ) : !selectedClass || !academicYear || !term ? (
            <Box sx={{ flex: 1, minHeight: 0, p: 8, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Select a class, academic year, and term to load the register.
              </Typography>
            </Box>
          ) : termAttendance.length === 0 ? (
            <Box sx={{ flex: 1, minHeight: 0, p: 8, textAlign: 'center' }}><Typography>No attendance data found for this class and term.</Typography></Box>
          ) : (
            <React.Fragment>
              <Box
                className="print-cover-page"
                sx={{
                  display: 'none',
                  '@media print': {
                    display: 'block !important',
                    width: '100%',
                    pageBreakAfter: 'always',
                    boxSizing: 'border-box',
                    pt: '12vh',
                    px: '10%',
                    bgcolor: 'white !important',
                    color: 'black !important',
                    visibility: 'visible !important'
                  }
                }}
              >
                <Typography variant="h3" align="center" fontWeight="900" sx={{ color: 'black !important', mb: 8, fontFamily: 'Arial, sans-serif', letterSpacing: '1px' }}>
                  GHANA EDUCATION SERVICE
                </Typography>

                <Box sx={{ borderBottom: '3px solid black', borderTop: '3px solid black', py: 4, mb: 10, width: '90%', mx: 'auto' }}>
                  <Typography variant="h4" align="center" fontWeight="bold" sx={{ color: 'black !important', fontFamily: '"Times New Roman", Times, serif', letterSpacing: '2px', lineHeight: 1.5 }}>
                    REGISTER OF<br />ATTENDANCE AND FEES
                  </Typography>
                </Box>

                <Box sx={{ width: '90%', mx: 'auto', mt: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(() => {
                    let sName = '';
                    let sCity = '';
                    let adminName = '';
                    try {
                      const raw = localStorage.getItem("user");
                      if (raw) {
                        const u = JSON.parse(raw);
                        sName = u.school?.name || u.schoolName || '';
                        sCity = u.school?.city || u.school?.town || u.city || '';
                        adminName = u.name || '';
                      }
                    } catch (e) { }

                    const activeClass = classes.find(c => c._id === selectedClass);
                    const cName = activeClass?.displayName || activeClass?.name || '';
                    const teacherName = activeClass?.classTeacher?.name || '';

                    const FieldRow = ({ value, label, leftLabel }) => (
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', width: '100%', '@media print': { display: 'flex !important', alignItems: 'flex-end !important' } }}>
                        {leftLabel && (
                          <Typography sx={{ fontSize: '1.4rem', mr: 2, fontFamily: '"Times New Roman", Times, serif', color: 'black !important' }}>
                            {leftLabel}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexGrow: 1, borderBottom: '2px dotted black', position: 'relative', minHeight: '2rem', '@media print': { borderBottom: '2px dotted black !important', display: 'flex !important' } }}>
                          <Typography
                            sx={{
                              position: 'absolute',
                              bottom: '4px',
                              left: '0',
                              width: '100%',
                              textAlign: 'center',
                              fontSize: '1.5rem',
                              fontFamily: 'Arial, sans-serif',
                              color: 'black !important',
                              fontWeight: 'bold'
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                        {label && (
                          <Typography sx={{ fontSize: '1.4rem', ml: 2, fontFamily: '"Times New Roman", Times, serif', color: 'black !important' }}>
                            {label}
                          </Typography>
                        )}
                      </Box>
                    );

                    return (
                      <React.Fragment>
                        <FieldRow value={sName} label="School" />
                        <FieldRow value={sCity} label="Town / City" />
                        <FieldRow value={cName} label="Class" />
                        <FieldRow leftLabel="For The Year Ending" value={academicYear} label="" />
                        <FieldRow value={teacherName} label="Class Teacher" />
                        <FieldRow value={adminName} label="Head Teacher" />
                      </React.Fragment>
                    );
                  })()}
                </Box>
              </Box>

              <DataTable sx={{
                flex: 1,
                minHeight: 0,
                maxWidth: '100%',
                '@media print': {
                  overflow: 'visible',
                  height: 'auto',
                  minHeight: 'auto'
                }
              }}>
                {Array.from({ length: maxWeeks }).map((_, wIndex) => {
                  const currentWeekToRender = wIndex + 1;
                  return (
                    <Box
                      key={currentWeekToRender}
                      sx={{
                        display: currentWeekToRender === registerActiveWeek ? 'block' : 'none',
                        '@media print': {
                          display: 'block !important',
                          pageBreakAfter: currentWeekToRender < maxWeeks ? 'always' : 'auto',
                          mb: 4,
                          pr: '4px',
                          boxSizing: 'border-box'
                        }
                      }}
                    >
                      <Box sx={{ display: 'none', '@media print': { display: 'block', mb: 2, textAlign: 'center', borderBottom: '2px solid black', paddingBottom: '8px' } }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'black' }}>
                          {term && academicYear
                            ? `Attendance Register - ${classes.find(c => c._id === selectedClass)?.displayName || classes.find(c => c._id === selectedClass)?.name || ''} - ${term} ${academicYear}`
                            : 'Full Term Attendance Register'}
                        </Typography>
                      </Box>
                      <Table size="small" sx={{
                        borderCollapse: 'collapse',
                        '& th, & td': { border: '1px solid #ccc', whiteSpace: 'nowrap' },
                        '@media print': {
                          border: '1px solid #000 !important',
                          WebkitPrintColorAdjust: 'exact',
                          printColorAdjust: 'exact',
                          width: '100% !important',
                          maxWidth: '100% !important',
                          tableLayout: 'fixed !important',
                          margin: '0 auto',
                          '& th, & td': {
                            borderColor: '#000 !important',
                            color: '#000 !important',
                            padding: '2px 1px !important',
                            fontSize: '0.62rem !important',
                            lineHeight: '1.05 !important',
                            verticalAlign: 'middle !important',
                          },
                          '& .register-number-cell': {
                            width: `${registerPrintWidths.number} !important`,
                            minWidth: '0 !important',
                            maxWidth: `${registerPrintWidths.number} !important`,
                          },
                          '& .register-admission-cell': {
                            width: `${registerPrintWidths.admission} !important`,
                            minWidth: '0 !important',
                            maxWidth: `${registerPrintWidths.admission} !important`,
                            whiteSpace: 'normal !important',
                            overflowWrap: 'anywhere',
                          },
                          '& .register-name-cell': {
                            width: `${registerPrintWidths.name} !important`,
                            minWidth: '0 !important',
                            maxWidth: `${registerPrintWidths.name} !important`,
                            whiteSpace: 'normal !important',
                            overflowWrap: 'anywhere',
                          },
                          '& .register-dob-cell': {
                            width: `${registerPrintWidths.dob} !important`,
                            minWidth: '0 !important',
                            maxWidth: `${registerPrintWidths.dob} !important`,
                            whiteSpace: 'normal !important',
                          },
                          '& .register-day-cell': {
                            width: `${registerPrintWidths.day} !important`,
                            minWidth: '0 !important',
                            maxWidth: `${registerPrintWidths.day} !important`,
                            padding: '2px 0 !important',
                            whiteSpace: 'nowrap !important',
                          },
                          '& .register-total-cell': {
                            width: `${registerPrintWidths.total} !important`,
                            minWidth: '0 !important',
                            maxWidth: `${registerPrintWidths.total} !important`,
                            padding: '2px 0 !important',
                          },
                          '& .register-status-icon': {
                            fontSize: '0.8rem !important',
                          },
                        }
                      }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'white' }}>
                            <TableCell className="register-number-cell" rowSpan={4} align="center" variant="head" sx={{ fontWeight: 'bold', border: '1px solid #ccc', minWidth: 50 }}>No.</TableCell>
                            <TableCell className="register-admission-cell" rowSpan={4} align="center" variant="head" sx={{ fontWeight: 'bold', border: '1px solid #ccc', minWidth: 100 }}>ADMISSION<br />NUMBER</TableCell>
                            <TableCell className="register-name-cell" rowSpan={4} align="left" variant="head" sx={{ minWidth: 250, fontWeight: 'bold', border: '1px solid #ccc' }}>NAME<br />(surname first)</TableCell>
                            <TableCell className="register-dob-cell" rowSpan={4} align="center" variant="head" sx={{ fontWeight: 'bold', border: '1px solid #ccc', minWidth: 100 }}>DATE OF<br />BIRTH</TableCell>
                            <TableCell colSpan={6} align="center" variant="head" sx={{ fontWeight: 'bold', bgcolor: '#fafafa', border: '1px solid #ccc' }}>
                              {currentWeekToRender}{['st', 'nd', 'rd'][(currentWeekToRender % 10) - 1] && currentWeekToRender !== 11 && currentWeekToRender !== 12 && currentWeekToRender !== 13 ? ['st', 'nd', 'rd'][(currentWeekToRender % 10) - 1] : 'th'} WEEK
                            </TableCell>
                          </TableRow>
                          {/* MONTH ROW */}
                          <TableRow sx={{ bgcolor: 'white' }}>
                            {(() => {
                              const weekInfo = termData?.weeks?.find(w => w.weekNumber === currentWeekToRender);
                              const monthName = weekInfo?.startDate ? new Date(weekInfo.startDate).toLocaleString('default', { month: 'long' }) : 'MONTH';
                              return (
                                <TableCell colSpan={6} align="center" variant="head" sx={{ fontWeight: 'bold', border: '1px solid #ccc', textTransform: 'uppercase', py: 0.5 }}>
                                  {monthName}
                                </TableCell>
                              );
                            })()}
                          </TableRow>
                          {/* DATE ROW */}
                          <TableRow sx={{ bgcolor: '#fff' }}>
                            {(() => {
                              const weekInfo = termData?.weeks?.find(w => w.weekNumber === currentWeekToRender);
                              const weekDayMap = new Map(
                                (weekInfo?.weekDays || []).map((weekDay) => [weekDay.dayName, weekDay]),
                              );
                              return (
                                <React.Fragment>
                                  {registerWeekdayOrder.map((dayName, di) => {
                                    const matchingDay = weekDayMap.get(dayName);
                                    const dateStr = formatOrdinalDay(matchingDay?.date);
                                    return (
                                      <TableCell className="register-day-cell" key={di} align="center" variant="head" sx={{ minWidth: 45, px: 0.5, border: '1px solid #ccc', py: 1.5, fontSize: '0.9rem', fontWeight: 'bold' }}>
                                        {dateStr}
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell className="register-total-cell" align="center" variant="head" sx={{ border: '1px solid #ccc', py: 1.5, minWidth: 40 }}></TableCell>
                                </React.Fragment>
                              );
                            })()}
                          </TableRow>
                          <TableRow sx={{ bgcolor: 'white' }}>
                            {['M', 'T', 'W', 'T', 'F'].map((d, di) => (
                              <TableCell className="register-day-cell" key={di} align="center" variant="head" sx={{ minWidth: 35, px: 0.5, fontWeight: 'bold', border: '1px solid #ccc' }}>{d === 'T' && di === 3 ? 'TH' : d}</TableCell>
                            ))}
                            <TableCell className="register-total-cell" align="center" variant="head" sx={{ minWidth: 40, px: 0.5, fontWeight: 'bold', border: '1px solid #ccc' }}>TOTAL</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {termAttendance.map((student, sIdx) => {
                            const weekData = student.attendance?.find(a => a.weekNumber === currentWeekToRender);
                            let studentCumulativeTotal = 0;
                            student.attendance?.forEach(a => {
                              if (a.weekNumber <= currentWeekToRender && a.days) {
                                studentCumulativeTotal += Object.values(a.days).filter(status => status === 'present').length;
                              }
                            });

                            return (
                              <TableRow key={student._id} sx={{ bgcolor: 'white', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }, '@media print': { bgcolor: 'white !important' } }}>
                                <TableCell className="register-number-cell" align="center" sx={{ border: '1px solid #ccc' }}>{sIdx + 1}</TableCell>
                                <TableCell className="register-admission-cell" align="center" sx={{ border: '1px solid #ccc' }}>{student.admissionNumber || '-'}</TableCell>
                                <TableCell className="register-name-cell" sx={{ border: '1px solid #ccc' }}>{student.name}</TableCell>
                                <TableCell className="register-dob-cell" align="center" sx={{ border: '1px solid #ccc' }}>
                                  {student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : '-'}
                                </TableCell>
                                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map((dayKey, dIdx) => {
                                  const apiDayKey = dayMap[dayKey];
                                  const status = weekData?.days?.[apiDayKey];

                                  return (
                                    <TableCell
                                      className="register-day-cell"
                                      key={dIdx}
                                      align="center"
                                      sx={{
                                        border: '1px solid #ccc',
                                        minWidth: 80,
                                        px: 0,
                                        bgcolor: 'white'
                                      }}
                                    >
                                      <Box display="flex" justifyContent="center">
                                        {status === 'present' ? (
                                          <CheckCircle className="register-status-icon" color="success" fontSize="small" />
                                        ) : status === 'absent' ? (
                                          <Cancel className="register-status-icon" color="error" fontSize="small" />
                                        ) : (
                                          <Typography variant="caption" color="text.disabled">-</Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                  );
                                })}
                                <TableCell
                                  className="register-total-cell"
                                  align="center"
                                  sx={{
                                    fontWeight: 'bold',
                                    border: '1px solid #ccc',
                                    minWidth: 40
                                  }}
                                >
                                  {studentCumulativeTotal}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {/* DETAILED DAILY TOTALS SECTION */}
                          {[
                            { label: 'Number Present (Boys)', gender: 'male' },
                            { label: 'Number Present (Girls)', gender: 'female' }
                          ].map((session, sIndex) => (
                            <TableRow key={sIndex} sx={{ bgcolor: 'white' }}>
                              <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>
                                {session.label.toUpperCase()}:
                              </TableCell>
                              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map((dayKey, dIdx) => {
                                const apiDayKey = dayMap[dayKey];
                                let count = 0;
                                termAttendance.forEach(student => {
                                  if (student.gender?.toLowerCase() === session.gender) {
                                    const weekDataInner = student.attendance?.find(a => a.weekNumber === currentWeekToRender);
                                    if (weekDataInner?.days?.[apiDayKey] === 'present') count++;
                                  }
                                });
                                return (
                                  <TableCell key={dIdx} align="center" sx={{ border: '1px solid #ccc' }}>
                                    {count || ''}
                                  </TableCell>
                                );
                              })}
                              <TableCell sx={{ border: '1px solid #ccc' }}></TableCell>
                            </TableRow>
                          ))}

                          {/* WEEKLY TOTALS SECTION */}
                          <TableRow sx={{ bgcolor: 'white' }}>
                            <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>
                              WEEKLY ATTENDANCES (BOYS):
                            </TableCell>
                            {(() => {
                              let weeklyBoysTotal = 0;
                              termAttendance.forEach(student => {
                                if (student.gender?.toLowerCase() === 'male') {
                                  const weekDataInner = student.attendance?.find(a => a.weekNumber === currentWeekToRender);
                                  weeklyBoysTotal += weekDataInner?.totalPresent || 0;
                                }
                              });
                              return (
                                <React.Fragment>
                                  <TableCell colSpan={5} sx={{ border: '1px solid #ccc' }}></TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ccc', color: 'primary.main' }}>
                                    {weeklyBoysTotal || ''}
                                  </TableCell>
                                </React.Fragment>
                              );
                            })()}
                          </TableRow>

                          <TableRow sx={{ bgcolor: 'white' }}>
                            <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>
                              WEEKLY ATTENDANCES (GIRLS):
                            </TableCell>
                            {(() => {
                              let weeklyGirlsTotal = 0;
                              termAttendance.forEach(student => {
                                if (student.gender?.toLowerCase() === 'female') {
                                  const weekDataInner = student.attendance?.find(a => a.weekNumber === currentWeekToRender);
                                  weeklyGirlsTotal += weekDataInner?.totalPresent || 0;
                                }
                              });
                              return (
                                <React.Fragment>
                                  <TableCell colSpan={5} sx={{ border: '1px solid #ccc' }}></TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ccc', color: 'primary.main' }}>
                                    {weeklyGirlsTotal || ''}
                                  </TableCell>
                                </React.Fragment>
                              );
                            })()}
                          </TableRow>

                          <TableRow sx={{ bgcolor: 'white' }}>
                            <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>
                              TIMES OPEN:
                            </TableCell>
                            {(() => {
                              // Helper to check if a day in a week has ANY attendance marks
                              const isDayOpen = (wNum, apiDay) => {
                                return termAttendance.some(student => {
                                  const weekDataInner = student.attendance?.find(a => a.weekNumber === wNum);
                                  return weekDataInner?.days?.[apiDay] === 'present' || weekDataInner?.days?.[apiDay] === 'absent';
                                });
                              };

                              // Count open days for current active week
                              let weeklyOpen = 0;
                              ['M', 'T', 'W', 'TH', 'F'].forEach(apiDay => {
                                if (isDayOpen(currentWeekToRender, apiDay)) weeklyOpen++;
                              });

                              // Fallback to instructional days if NO attendance has been marked at all (to avoid showing 0 for empty weeks)
                              if (weeklyOpen === 0 && termData?.weeks) {
                                const weekInfo = termData.weeks.find(w => w.weekNumber === currentWeekToRender);
                                weeklyOpen = weekInfo?.instructionalDays || 0;
                              }

                              // Calculate cumulative up to active week
                              let cumulativeOpen = 0;
                              for (let i = 1; i <= currentWeekToRender; i++) {
                                let weekOpenCount = 0;
                                ['M', 'T', 'W', 'TH', 'F'].forEach(apiDay => {
                                  if (isDayOpen(i, apiDay)) weekOpenCount++;
                                });

                                // If no marks in this week, use scheduled instructional days
                                if (weekOpenCount === 0 && termData?.weeks) {
                                  const wInfo = termData.weeks.find(w => w.weekNumber === i);
                                  weekOpenCount = wInfo?.instructionalDays || 0;
                                }
                                cumulativeOpen += weekOpenCount;
                              }

                              return (
                                <React.Fragment>
                                  <TableCell colSpan={5} align="center" sx={{ border: '1px solid #ccc' }}>{weeklyOpen}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>{cumulativeOpen}</TableCell>
                                </React.Fragment>
                              );
                            })()}
                          </TableRow>

                          <TableRow sx={{ bgcolor: 'white' }}>
                            <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', border: '1px solid #ccc', color: 'primary.main' }}>
                              TOTAL ATTENDANCES:
                            </TableCell>
                            {(() => {
                              let combinedTotal = 0;
                              termAttendance.forEach(student => {
                                const weekDataInner = student.attendance?.find(a => a.weekNumber === currentWeekToRender);
                                combinedTotal += weekDataInner?.totalPresent || 0;
                              });
                              return (
                                <React.Fragment>
                                  <TableCell colSpan={5} sx={{ border: '1px solid #ccc' }}></TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>
                                    {combinedTotal || ''}
                                  </TableCell>
                                </React.Fragment>
                              );
                            })()}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  );
                })}
              </DataTable>
            </React.Fragment>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
